import {
  ROLE_HIERARCHY,
  getRoleLabel as getLabel,
  hasAnyRole,
  hasMinimumRole,
  hasRole,
  isDataOwner,
  isOrgAdmin,
  isSuperAdmin,
} from '@/lib/roles';
import { Role } from '@/types';
import { useMemo } from 'react';

import { useAuthStatus } from './useAuthStatus';

type AllowedRolesByOrg = Record<string, Role[]>;
type AssumedRolesByOrg = Record<string, Role>;

type UseRolesReturn = {
  // Permission checks (based on ASSUMED role)
  hasRole: (orgId: string, role: Role) => boolean;
  hasAnyRole: (orgId: string, roles: Role[]) => boolean;
  hasMinimumRole: (orgId: string, role: Role) => boolean;
  isSuperAdmin: (orgId: string) => boolean;
  isOrgAdmin: (orgId: string) => boolean;
  isDataOwner: (orgId: string) => boolean;

  // Global admin status (platform-wide, not org-specific)
  isGlobalSuperAdmin: () => boolean;

  // Role info
  getCurrentRole: (orgId: string) => Role | undefined;
  getAllowedRoles: (orgId: string) => Role[];
  getHighestRole: (orgId: string) => Role | undefined; // Highest ALLOWED role
  getRoleLabel: (orgId: string) => string | null; // Label for CURRENT role

  // Raw data
  allowedRolesByOrg: AllowedRolesByOrg;
  assumedRolesByOrg: AssumedRolesByOrg;
  isAuthenticated: boolean;
};

type UseRolesScopedReturn = {
  // Permission checks (based on ASSUMED role)
  hasRole: (role: Role) => boolean;
  hasAnyRole: (roles: Role[]) => boolean;
  hasMinimumRole: (role: Role) => boolean;
  isSuperAdmin: () => boolean;
  isOrgAdmin: () => boolean;
  isDataOwner: () => boolean;

  // Global admin status (platform-wide, not org-specific)
  isGlobalSuperAdmin: () => boolean;

  // Role info
  getCurrentRole: () => Role | undefined;
  getAllowedRoles: () => Role[];
  getHighestRole: () => Role | undefined; // Highest ALLOWED role
  getRoleLabel: () => string | null; // Label for CURRENT role

  // Raw data
  allowedRolesByOrg: AllowedRolesByOrg;
  assumedRolesByOrg: AssumedRolesByOrg;
  isAuthenticated: boolean;
};

/**
 * Custom hook for org-aware role-based authorization checks.
 * Provides convenient methods to check user roles and permissions for a given org.
 *
 * @overload
 * @returns Object with role checking methods that require orgId parameter
 *
 * @overload
 * @param orgId - Organization ID to scope all role checks to
 * @returns Object with role checking methods scoped to the provided orgId
 *
 * @example
 * // Pattern 1: Org-agnostic (requires orgId in each call)
 * const { hasRole, hasAnyRole } = useRoles();
 * if (hasRole(orgId, 'SuperAdmin')) { ... }
 *
 * @example
 * // Pattern 2: Org-scoped (orgId provided upfront)
 * const { hasRole, hasAnyRole } = useRoles(orgId);
 * if (hasRole('SuperAdmin')) { ... }
 */
export function useRoles(): UseRolesReturn;
export function useRoles(orgId: string | undefined): UseRolesScopedReturn;
export function useRoles(scopedOrgId?: string) {
  const { user } = useAuthStatus();

  const allowedRolesByOrg: AllowedRolesByOrg = useMemo(() => {
    // If allowed_roles is missing but user has assumed_roles, use assumed roles as fallback
    // This handles global super admins who might not have explicit allowed_roles
    if (user?.allowed_roles) {
      return user.allowed_roles;
    }

    if (user?.assumed_roles) {
      // Convert assumed_roles (Record<string, Role>) to allowed_roles format (Record<string, Role[]>)
      const fallbackAllowedRoles: AllowedRolesByOrg = {};
      Object.entries(user.assumed_roles).forEach(([org, role]) => {
        fallbackAllowedRoles[org] = [role];
      });
      return fallbackAllowedRoles;
    }

    return {};
  }, [user?.allowed_roles, user?.assumed_roles]);

  const assumedRolesByOrg: AssumedRolesByOrg = useMemo(
    () => (user?.assumed_roles ? user.assumed_roles : {}),
    [user?.assumed_roles],
  );

  const isAuthenticated = !!user;

  const roleChecks = useMemo(
    () => ({
      hasRole: (id: string, role: Role) => hasRole(assumedRolesByOrg[id], role),
      hasAnyRole: (id: string, roles: Role[]) => hasAnyRole(assumedRolesByOrg[id], roles),
      hasMinimumRole: (id: string, role: Role) => hasMinimumRole(assumedRolesByOrg[id], role),
      isSuperAdmin: (id: string) => isSuperAdmin(assumedRolesByOrg[id]),
      isOrgAdmin: (id: string) => isOrgAdmin(assumedRolesByOrg[id]),
      isDataOwner: (id: string) => isDataOwner(assumedRolesByOrg[id]),
      getCurrentRole: (id: string) => assumedRolesByOrg[id],
      getAllowedRoles: (id: string) => allowedRolesByOrg[id] ?? [],
      getHighestRole: (id: string) => {
        const roles = allowedRolesByOrg[id] ?? [];
        if (!roles.length) return undefined;
        // Sort by hierarchy (index 0 is highest)
        return roles.slice().toSorted((a, b) => ROLE_HIERARCHY.indexOf(a) - ROLE_HIERARCHY.indexOf(b))[0];
      },
      getRoleLabel: (id: string) => {
        const currentRole = assumedRolesByOrg[id];
        return getLabel(currentRole);
      },
      isGlobalSuperAdmin: () => user?.is_super_admin ?? false,
    }),
    [allowedRolesByOrg, assumedRolesByOrg, user],
  );

  // If scopedOrgId is provided and valid, return scoped version
  if (scopedOrgId !== undefined && scopedOrgId !== '') {
    const scopedReturn: UseRolesScopedReturn = {
      hasRole: (role: Role) => roleChecks.hasRole(scopedOrgId, role),
      hasAnyRole: (roles: Role[]) => roleChecks.hasAnyRole(scopedOrgId, roles),
      hasMinimumRole: (role: Role) => roleChecks.hasMinimumRole(scopedOrgId, role),
      isSuperAdmin: () => roleChecks.isSuperAdmin(scopedOrgId),
      isOrgAdmin: () => roleChecks.isOrgAdmin(scopedOrgId),
      isDataOwner: () => roleChecks.isDataOwner(scopedOrgId),
      getCurrentRole: () => roleChecks.getCurrentRole(scopedOrgId),
      getAllowedRoles: () => roleChecks.getAllowedRoles(scopedOrgId),
      getHighestRole: () => roleChecks.getHighestRole(scopedOrgId),
      getRoleLabel: () => roleChecks.getRoleLabel(scopedOrgId),
      isGlobalSuperAdmin: roleChecks.isGlobalSuperAdmin,
      allowedRolesByOrg,
      assumedRolesByOrg,
      isAuthenticated,
    };
    return scopedReturn;
  }

  // Otherwise, return org-agnostic version
  const agnosticReturn: UseRolesReturn = {
    allowedRolesByOrg,
    assumedRolesByOrg,
    isAuthenticated,
    ...roleChecks,
  };
  return agnosticReturn;
}

/**
 * Type guard to check if a user has a specific role value.
 * Useful for conditional rendering and type narrowing.
 */
export function isRole(role: Role | undefined, expectedRole: Role): role is Role {
  return role === expectedRole;
}

/**
 * Hook to require specific roles for a component in a given org.
 * Returns false if user doesn't have required role(s) for the org.
 *
 * @param orgId - Organization ID to check roles for.
 * @param requiredRoles - Single role or array of acceptable roles.
 * @returns true if user has required role(s) in the org, false otherwise.
 */
export function useRequireRoles(orgId: string, requiredRoles: Role | Role[]): boolean {
  const { hasAnyRole: checkAnyRole, hasRole: checkRole } = useRoles(orgId);

  if (Array.isArray(requiredRoles)) {
    return checkAnyRole(requiredRoles);
  }
  return checkRole(requiredRoles);
}
